// File: src/PassportUploadPage.js
// Passport upload page for non-UK drivers after POA validation
// Oct 2026 redesign: StepShell (Final checks & sign) + hf- building blocks
// (src/ui/HireFormUI.js). The iDenfy hand-off below is unchanged.
// Session 78: Removed excess display - will show on signature page instead

import React, { useState } from 'react';
import { StepShell, Card, PrimaryButton, Note, Why, PHASE } from './ui/HireFormUI';

const PassportUploadPage = ({ driverEmail, jobId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startPassportUpload = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🛂 Starting passport upload session for:', driverEmail);

      // Create Idenfy session for passport upload only
      const response = await fetch('/.netlify/functions/create-idenfy-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: driverEmail,
          jobId: jobId,
          verificationType: 'passport_only',
          isUKDriver: false,
          // iDenfy must send the driver back to THIS origin. sessionStorage
          // (the session token) is per-origin, and without this the function
          // fell back to the netlify.app domain — so a driver who started on
          // hireforms.oooshtours.co.uk came back logged out after their
          // passport check. Same fix as the licence check (App.js, v3.9).
          origin: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create passport upload session');
      }

      const data = await response.json();
      
      if (data.sessionToken) {
        console.log('✅ Passport session created, redirecting to Idenfy');
        // Redirect to Idenfy for passport upload
        window.location.href = data.redirectUrl || 
          `https://ui.idenfy.com/session?authToken=${data.sessionToken}&documentType=PASSPORT`;
      } else {
        throw new Error('No session token received');
      }

    } catch (err) {
      console.error('Error creating passport session:', err);
      setError("We couldn't start the passport check. Please try again.");
      setLoading(false);
    }
  };

  // App passes jobId; the URL is the fallback so the header still names the job.
  const shellJobId = jobId || new URLSearchParams(window.location.search).get('job');

  return (
    <StepShell jobId={shellJobId} phase={PHASE.SIGN}>
      <Card>
        <h2 className="hf-h2">Check your passport</h2>
        {/* A driver lands here for EITHER reason: a non-UK licence, or a UK
            licence whose proofs of address show a different address. */}
        <p className="hf-intro">
          We need to check your passport to finish your hire form. Our insurer asks for this when
          your licence wasn&rsquo;t issued in the UK, or when your proofs of address show a different
          address from your licence.
        </p>

        <ol className="hf-numlist">
          <li><span className="hf-dot">1</span><span>A photo of your passport&rsquo;s photo page</span></li>
          <li><span className="hf-dot">2</span><span>A few seconds of selfie video</span></li>
        </ol>

        <p className="hf-inset hf-body">
          Open the passport flat, include the two lines of code at the bottom, and keep your fingers
          off the photo.
        </p>

        {error && <Note tone="error">{error}</Note>}

        <Why lead="What happens next:">
          a secure page from our ID partner, iDenfy, opens. When you finish, you&rsquo;ll come straight
          back here and go on to the next step. Please don&rsquo;t close the page in between.
        </Why>

        <PrimaryButton onClick={startPassportUpload} busy={loading} busyLabel="Opening…">
          Start passport check
        </PrimaryButton>
      </Card>
    </StepShell>
  );
};

export default PassportUploadPage;
